import { RbacActions } from '@supply-mover-2-0/shared';
import type { BulkActionConfig } from './types/list';

const clientUpdatePermission: Array<string> = [
	RbacActions.CLIENTS.LEADS.UPDATE.action,
	RbacActions.CLIENTS.CUSTOMERS.UPDATE.action,
];

/**
 * PD-16654 — the Duplicate License / Shared License lists' bulk review: pick
 * several groups (a row's checkbox takes its whole group) and record one
 * outcome and one note for all of them. licenses:update, as the single-group
 * review and the Adverse Action mark-off.
 */
export const COMPLIANCE_DUPLICATE_GROUP_BULK_TEMPLATE =
	'compliance-duplicate-groups';

/**
 * PD-16825 — Sales > Lead Finder: import the selected pharmacy locations as
 * leads (up to 500 at a time) through the import framework.
 */
export const LEAD_FINDER_BULK_TEMPLATE = 'lead-finder';

/** PD-16825 — the Lead Finder's Dismissed tab: put locations back in the results. */
export const LEAD_FINDER_DISMISSED_BULK_TEMPLATE = 'lead-finder-dismissed';

const bulkActionTemplates: Record<string, Array<BulkActionConfig>> = {
	[LEAD_FINDER_BULK_TEMPLATE]: [
		{
			id: 'import-leads',
			label: 'Import as Leads',
			callback: 'importLeads',
			permission: RbacActions.LEAD_FINDER.IMPORT.action,
		},
		{
			id: 'dismiss-leads',
			label: 'Dismiss (not a fit)',
			callback: 'dismissLeads',
			permission: RbacActions.LEAD_FINDER.IMPORT.action,
		},
	],
	[LEAD_FINDER_DISMISSED_BULK_TEMPLATE]: [
		{
			id: 'restore-leads',
			label: 'Restore to results',
			callback: 'restoreLeads',
			permission: RbacActions.LEAD_FINDER.IMPORT.action,
		},
	],
	[COMPLIANCE_DUPLICATE_GROUP_BULK_TEMPLATE]: [
		{
			id: 'review-duplicate-groups',
			label: 'Review Selected Groups',
			callback: 'reviewDuplicateGroups',
			permission: RbacActions.LICENSES.UPDATE.action,
		},
	],
	'lead-eligibility': [
		{
			id: 'bulk-edit',
			label: 'Bulk Edit',
			callback: 'bulkEditEligibility',
			// PD-14923 — dedicated manage action is the sole edit gate
			// (user:update does not grant it).
			permission: RbacActions.USER.LEAD_ELIGIBILITY_MANAGE.action,
		},
	],
	clients: [
		{
			id: 'add-to-list',
			label: 'Add to List',
			callback: 'addToList',
			permission: clientUpdatePermission,
		},
		{
			id: 'assign-sales-rep',
			label: 'Assign Sales Rep',
			callback: 'assignSalesRep',
			permission: clientUpdatePermission,
		},
		{
			id: 'unassign-sales-rep',
			label: 'Unassign Sales Rep',
			callback: 'unassignSalesRep',
			permission: clientUpdatePermission,
		},
		{
			id: 'assign-alternate-rep',
			label: 'Assign Alternate Rep',
			callback: 'assignAlternateRep',
			permission: clientUpdatePermission,
		},
		{
			id: 'unassign-alternate-rep',
			label: 'Unassign Alternate Rep',
			callback: 'unassignAlternateRep',
			permission: clientUpdatePermission,
		},
		{
			id: 'assign-to-group',
			label: 'Assign to Group',
			callback: 'assignToGroup',
			permission: clientUpdatePermission,
		},
		{
			id: 'remove-from-group',
			label: 'Remove from Group',
			callback: 'removeFromGroup',
			permission: clientUpdatePermission,
		},
		{
			id: 'change-lifecycle-stage',
			label: 'Change Lifecycle Stage',
			callback: 'changeLifecycleStage',
			permission: clientUpdatePermission,
		},
		{
			id: 'distribute-leads',
			label: 'Distribute Leads',
			callback: 'distributeLeads',
			permission: clientUpdatePermission,
		},
		{
			// PD-16874 — deleting a lead takes Delete Own Leads or Delete Any
			// Lead (not Edit Leads); a customer still takes Edit Customers. The
			// server drops each selected client the caller may not delete.
			id: 'delete-clients',
			label: 'Delete Clients',
			callback: 'deleteClients',
			permission: [
				RbacActions.CLIENTS.LEADS.DELETE_SELF.action,
				RbacActions.CLIENTS.LEADS.DELETE.action,
				RbacActions.CLIENTS.CUSTOMERS.UPDATE.action,
			],
		},
		{
			id: 'undelete-clients',
			label: 'Recover Clients',
			callback: 'undeleteClients',
			permission: clientUpdatePermission,
		},
		{
			// Unified bulk-edit covers both native client columns AND
			// custom fields. Permission union (canAny) preserves both
			// prior audiences: users with client-update perm can edit
			// native columns, users with custom-field-edit perm can edit
			// custom fields, and users with both see one menu item
			// instead of two.
			id: 'bulk-edit',
			label: 'Bulk Edit',
			callback: 'bulkEdit',
			permission: [
				...clientUpdatePermission,
				RbacActions.CUSTOM_FIELDS.VALUES.EDIT.action,
			],
		},
	],
	priceContractClients: [
		{
			id: 'remove-from-contract',
			label: 'Remove from Contract',
			callback: 'removeFromContract',
			permission: RbacActions.PRICE_CONTRACTS.CLIENTS.DELETE.action,
		},
	],
	// PD-14228 — ownership detail Clients tab: bulk-remove from the ownership.
	ownershipClients: [
		{
			id: 'remove-from-ownership',
			label: 'Remove from Ownership',
			callback: 'removeFromOwnership',
			permission: clientUpdatePermission,
		},
	],
	'sourcing-requests': [
		{
			id: 'assign-user',
			label: 'Assign User',
			callback: 'assignUser',
			permission: [RbacActions.SOURCING_REQUESTS.BULK_ASSIGN.action],
		},
		{
			id: 'add-watcher',
			label: 'Add Watcher',
			callback: 'addWatcher',
			permission: [RbacActions.SOURCING_REQUESTS.BULK_ASSIGN.action],
		},
		{
			id: 'change-priority',
			label: 'Change Priority',
			callback: 'changePriority',
			permission: [RbacActions.SOURCING_REQUESTS.BULK_ASSIGN.action],
		},
		{
			id: 'update-target-date',
			label: 'Update Target Date',
			callback: 'updateTargetDate',
			permission: [RbacActions.SOURCING_REQUESTS.BULK_ASSIGN.action],
		},
	],
	// PD-15839: the Items list's hand-picked item lists. Adding an item to a
	// list does not change the item, so View Items is enough — the per-list
	// manager / allow-edit rule is enforced server-side.
	items: [
		{
			id: 'add-to-list',
			label: 'Add to List',
			callback: 'addToList',
			permission: RbacActions.ITEMS.READ.action,
		},
	],
	priceContractItems: [
		// SAVED_PRICES.CREATE is included so sales-rep-style users who manage
		// per-client saved prices without holding the broader ITEMS.UPDATE /
		// ITEMS.DELETE can still see the bulk actions. They can only reach the
		// items list on a saved-prices contract (module config gates the tab
		// visibility), so showing these here is contract-appropriate.
		{
			id: 'update-pricing',
			label: 'Edit',
			callback: 'editPricing',
			permission: [
				RbacActions.PRICE_CONTRACTS.ITEMS.UPDATE.action,
				RbacActions.PRICE_CONTRACTS.SAVED_PRICES.CREATE.action,
			],
		},
		{
			id: 'remove-from-contract',
			label: 'Remove from Contract',
			callback: 'removeFromContract',
			permission: [
				RbacActions.PRICE_CONTRACTS.ITEMS.DELETE.action,
				RbacActions.PRICE_CONTRACTS.SAVED_PRICES.CREATE.action,
			],
		},
	],
};

export function getBulkActionTemplate(key: string): Array<BulkActionConfig> {
	return bulkActionTemplates[key] ?? [];
}
